import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  minimizeCommentMutation,
  minimizeCommentMutation$rawResponse,
  ReportedContentClassifiers,
} from './__generated__/minimizeCommentMutation.graphql'

export function minimizeCommentMutation({
  environment,
  input: {id, reason},
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {id: string; reason: ReportedContentClassifiers}
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  const optimisticResponse: minimizeCommentMutation$rawResponse = {
    minimizeComment: {
      clientMutationId: '',
      minimizedComment: {
        id,
        __typename: 'IssueComment',
        __isNode: 'IssueComment',
        isMinimized: true,
        minimizedReason: reason,
      },
    },
  }

  const optimistic = publishOptimisticFields(id, {isMinimized: true, minimizedReason: reason})

  let disposable
  try {
    disposable = commitMutation<minimizeCommentMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation minimizeCommentMutation($input: MinimizeCommentInput!) @raw_response_type {
          minimizeComment(input: $input) {
            clientMutationId
            minimizedComment {
              isMinimized
              minimizedReason
            }
          }
        }
      `,
      variables: {
        input: {subjectId: id, classifier: reason},
      },
      optimisticResponse,
      onCompleted: response => {
        const minimized = response.minimizeComment?.minimizedComment
        if (!minimized) {
          optimistic.failed(new Error('Failed to minimize comment'))
          onError?.()
          return
        }
        optimistic.completed({
          isMinimized: minimized.isMinimized,
          minimizedReason: minimized.minimizedReason,
        })
        onCompleted?.()
      },
      onUnsubscribe: () => optimistic.failed(new Error('Minimizing the comment was cancelled')),
      onError: error => {
        optimistic.failed(error)
        onError?.(error)
      },
    })
  } catch (error) {
    optimistic.failed(error instanceof Error ? error : new Error('Failed to minimize comment'))
    throw error
  }

  return disposable
}
